/**
 * Review & Publish — the wizard's final step (#272, UX-review finding 8).
 *
 * A read-only recap of every earlier step with jump-to-edit links, the
 * deferred Status control (moved here from Merchandising — "what this will be
 * created as" belongs beside the button that creates it), and any submit
 * errors mapped back to the step that owns them. Reads the live form via
 * context; owns no state and changes no submit behaviour.
 */
import { useFormContext, useWatch } from "react-hook-form";
import { Button } from "@flint/ui";
import { paiseToInrExact } from "../../../lib/format.ts";
import { StatusSegments } from "./status-segments.tsx";
import { usableEntries, type PricingEntry } from "./size-ratio.ts";

/** Which step owns which form field — submit errors jump there. */
export const FIELD_STEP: Record<string, number> = {
  name: 0,
  sku: 0,
  hsn: 0,
  category: 0,
  subCategory: 0,
  collection: 0,
  description: 0,
  variantAttributes: 2,
  variants: 3,
  // Inventory (4) sits BEFORE Bundle Builder (5) — user decision 2026-08-13:
  // stock is declared first, bundles are composed from it.
  openingWarehouseId: 4,
  openingQty: 4,
  pricingEntries: 5,
  taxPct: 5,
  piecesPerPack: 5,
  sizeRatio: 5,
  shortDesc: 6,
  longDesc: 6,
  brand: 6,
  countryOfOrigin: 6,
  material: 6,
  gsm: 6,
  colour: 6,
  care: 6,
};

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-1.5 text-sm last:border-b-0">
      <span className="shrink-0 text-muted">{label}</span>
      {/* Numbers are mono (brand spec §3.4) — the `mono` rows carry figures. */}
      <span className={`truncate text-right text-ink-2${mono ? " font-mono tabular-nums" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}

function Group({
  title,
  step,
  onJump,
  children,
}: {
  title: string;
  step: number;
  onJump: (step: number) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded border border-line bg-paper px-4 py-3 shadow">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-ink">{title}</span>
        <button
          type="button"
          className="text-xs font-medium text-accent hover:underline"
          onClick={() => onJump(step)}
        >
          Edit
        </button>
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export function ProductReviewStep({
  imagesCount,
  warehouseName = null,
  createAs = "draft",
  onCreateAsChange,
  errors,
  busy = false,
  onJump,
  onCreate,
  fieldStep = FIELD_STEP,
  showInventory = true,
  footer,
}: {
  imagesCount: number;
  /** Display name of the chosen opening-stock warehouse, if any. */
  warehouseName?: string | null;
  createAs?: "draft" | "published";
  onCreateAsChange?: (next: "draft" | "published") => void;
  /** Field-name → message pairs from the last failed submit. */
  errors: readonly { field: string; message: string }[];
  busy?: boolean;
  onJump: (step: number) => void;
  onCreate?: () => void;
  /** Field → step map; the EDIT wizard has no Inventory step, so it differs. */
  fieldStep?: Record<string, number>;
  /** False on the edit wizard — opening stock is create-only. */
  showInventory?: boolean;
  /**
   * Replaces the built-in "Create as + action" block — the edit wizard brings
   * its own staged-status control and Save button.
   */
  footer?: React.ReactNode;
}) {
  const { control } = useFormContext();
  const v = useWatch({ control }) as Record<string, unknown>;

  const s = (key: string) => ((v[key] as string) ?? "").trim();
  const entries = usableEntries((v.pricingEntries as PricingEntry[]) ?? []);
  const axes = ((v.variantAttributes as { name: string; values: string[] }[]) ?? []).filter(
    (a) => a.name.trim() && a.values.length,
  );
  const variantCount = ((v.variants as unknown[]) ?? []).length;

  return (
    <div className="flex flex-col gap-3">
      {errors.length > 0 ? (
        <div role="alert" className="rounded border border-rose-200 bg-rose-50 p-3 text-sm">
          <p className="font-medium text-bad">Fix before creating:</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {errors.map((e) => (
              <li key={e.field}>
                <button
                  type="button"
                  className="text-bad underline"
                  onClick={() => onJump(fieldStep[e.field] ?? 0)}
                >
                  {e.message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-2">
        <Group title="Basic information" step={fieldStep.name ?? 0} onJump={onJump}>
          <Row label="Name" value={s("name")} />
          <Row label="SKU" value={s("sku")} />
          <Row label="HSN" value={s("hsn")} />
          <Row
            label="Category"
            value={[s("category"), s("subCategory")].filter(Boolean).join(" · ")}
          />
        </Group>

        <Group title="Images" step={1} onJump={onJump}>
          <Row label="Uploaded" value={imagesCount ? `${imagesCount} image${imagesCount > 1 ? "s" : ""}` : ""} />
        </Group>

        <Group title="Attributes & variants" step={fieldStep.variants ?? 3} onJump={onJump}>
          <Row
            label="Axes"
            value={axes.map((a) => `${a.name.trim()} (${a.values.length})`).join(" · ")}
          />
          <Row label="Variants" value={variantCount ? `${variantCount} generated` : ""} />
        </Group>

        {showInventory ? (
          <Group title="Inventory" step={fieldStep.openingQty ?? 4} onJump={onJump}>
            <Row
              label="Opening stock"
              value={
                s("openingQty") && warehouseName ? `${s("openingQty")} pcs · ${warehouseName}` : ""
              }
              mono
            />
          </Group>
        ) : null}

        <Group title="Bundle Builder" step={fieldStep.pricingEntries ?? 5} onJump={onJump}>
          {entries.length ? (
            entries.map((e) => (
              <Row
                key={e.label}
                label={e.label}
                // The same figure the dialog's "Price (₹ per pack)" field holds.
                value={`${paiseToInrExact(e.pricePaise)} per pack`}
                mono
              />
            ))
          ) : (
            <Row label="Bundles" value="" />
          )}
          <Row label="GST" value={s("taxPct") ? `${s("taxPct")}%` : ""} mono />
        </Group>

        <Group title="Merchandising" step={fieldStep.shortDesc ?? 6} onJump={onJump}>
          <Row label="Short description" value={s("shortDesc")} />
          <Row label="Brand" value={s("brand")} />
          <Row
            label="Fabric / GSM / Colour"
            value={[s("material"), s("gsm"), s("colour")].filter(Boolean).join(" · ")}
          />
        </Group>
      </div>

      {footer ?? (
        <div className="mt-1 flex flex-wrap items-end justify-between gap-4 rounded border border-line bg-paper px-4 py-3 shadow">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium leading-[18px] text-ink-2">Create as</span>
            {/* Deferred status: nothing exists to transition yet, so the pick is
                applied at save (moved here from Merchandising with #272 — it
                belongs beside the button it changes the meaning of). */}
            <StatusSegments
              current={createAs}
              onPick={(st) => {
                if (st !== "archived") onCreateAsChange?.(st);
              }}
              unavailable={{ archived: "Available once the product exists" }}
            />
            <p className="text-xs text-muted">Applied when the product is created.</p>
          </div>
          <Button intent="primary" size="md" disabled={busy} onClick={onCreate}>
            {createAs === "published" ? <>Create &amp; publish</> : <>Save as draft</>}
          </Button>
        </div>
      )}
    </div>
  );
}
